import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, Chip, Button, Alert, IconButton, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip, CircularProgress
} from '@mui/material';
import { Add, Edit, Delete, Home, AccountTree, ListAlt, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DimensionsList = () => {
  const navigate = useNavigate();
  const [dims, setDims]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // Dimension create/edit dialog
  const [dlg, setDlg]   = useState(null);   // { editing?, code, name, description }
  const [confirmDim, setConfirmDim] = useState(null);

  // Values manager
  const [valuesFor, setValuesFor] = useState(null); // dimension object
  const [values, setValues]       = useState([]);
  const [valDlg, setValDlg]       = useState(null); // { editing?, _id?, value, name }
  const dimGuard = useUnsavedGuard({ open: Boolean(dlg),    value: dlg,    onClose: () => setDlg(null) });
  const valGuard = useUnsavedGuard({ open: Boolean(valDlg), value: valDlg, onClose: () => setValDlg(null) });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/finance/dimensions`, { headers: hdrs() });
      setDims(data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load dimensions'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 1800); };

  const saveDim = async () => {
    if (!dlg.code?.trim() || !dlg.name?.trim()) { setError('Code and Name are required'); return; }
    try {
      if (dlg.editing) await axios.put(`${API}/api/finance/dimensions/${dlg.code}`, { name: dlg.name, description: dlg.description }, { headers: hdrs() });
      else await axios.post(`${API}/api/finance/dimensions`, dlg, { headers: hdrs() });
      setDlg(null); flash('Saved'); load();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
  };
  const deleteDim = async () => {
    try { await axios.delete(`${API}/api/finance/dimensions/${confirmDim.code}`, { headers: hdrs() }); setConfirmDim(null); flash('Deleted'); load(); }
    catch (err) { setConfirmDim(null); setError(err.response?.data?.error || 'Delete failed'); }
  };

  const openValues = async (dim) => {
    setValuesFor(dim);
    try { const { data } = await axios.get(`${API}/api/finance/dimension-values`, { headers: hdrs(), params: { dimension: dim.code } }); setValues(data); }
    catch { setValues([]); }
  };
  const loadValues = async () => {
    const { data } = await axios.get(`${API}/api/finance/dimension-values`, { headers: hdrs(), params: { dimension: valuesFor.code } });
    setValues(data);
  };
  const saveValue = async () => {
    if (!valDlg.value?.trim() || !valDlg.name?.trim()) { setError('Value and Name are required'); return; }
    try {
      if (valDlg.editing) await axios.put(`${API}/api/finance/dimension-values/${valDlg._id}`, { name: valDlg.name }, { headers: hdrs() });
      else await axios.post(`${API}/api/finance/dimension-values`, { dimensionCode: valuesFor.code, value: valDlg.value, name: valDlg.name }, { headers: hdrs() });
      setValDlg(null); loadValues();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
  };
  const deleteValue = async (id) => {
    try { await axios.delete(`${API}/api/finance/dimension-values/${id}`, { headers: hdrs() }); loadValues(); }
    catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  if (loading) return <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 6 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <AccountTree color="primary" />
              <Typography variant="h5" color="primary">Dimensions</Typography>
              <Chip label={dims.length} size="small" color="primary" />
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={() => navigate('/setups')} title="Setups"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
              <Button variant="contained" startIcon={<Add />}
                onClick={() => setDlg({ code: '', name: '', description: '' })}>Add Dimension</Button>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          <Typography variant="body2" color="text.secondary" mb={2}>
            Analysis axes for accounting (Department, Project, Fund, Campus…). Capture them on the General
            Journal and slice the GL by dimension.
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dims.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center"><Typography variant="body2" color="text.secondary" py={2}>No dimensions yet.</Typography></TableCell></TableRow>
              ) : dims.map(d => (
                <TableRow key={d.code} hover>
                  <TableCell><strong>{d.code}</strong></TableCell>
                  <TableCell>{d.name}</TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{d.description || '—'}</Typography></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Values"><IconButton size="small" color="primary" onClick={() => openValues(d)}><ListAlt fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => setDlg({ editing: true, code: d.code, name: d.name, description: d.description || '' })}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setConfirmDim(d)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dimension create/edit */}
      {dimGuard.discardDialog}
      <Dialog open={Boolean(dlg)} onClose={dimGuard.attemptClose} maxWidth="xs" fullWidth>
        <DialogTitle>{dlg?.editing ? `Edit — ${dlg.code}` : 'Add Dimension'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Code" value={dlg?.code || ''} disabled={dlg?.editing}
              onChange={e => setDlg({ ...dlg, code: e.target.value.toUpperCase() })}
              inputProps={{ style: { textTransform: 'uppercase' } }} size="small" helperText="e.g. DEPARTMENT" />
            <TextField label="Name" value={dlg?.name || ''} onChange={e => setDlg({ ...dlg, name: e.target.value })} size="small" />
            <TextField label="Description" value={dlg?.description || ''} onChange={e => setDlg({ ...dlg, description: e.target.value })} size="small" multiline rows={2} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={dimGuard.attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={saveDim}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete dimension */}
      <Dialog open={Boolean(confirmDim)} onClose={() => setConfirmDim(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Delete Dimension
        </DialogTitle>
        <DialogContent><Typography>Delete <strong>{confirmDim?.code} — {confirmDim?.name}</strong> and all its values?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDim(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={deleteDim}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Values manager */}
      <Dialog open={Boolean(valuesFor)} onClose={() => setValuesFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <span>{valuesFor?.code} — Values</span>
            <Button size="small" startIcon={<Add />} onClick={() => setValDlg({ value: '', name: '' })}>Add Value</Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {values.length === 0 ? <Typography variant="body2" color="text.secondary">No values yet.</Typography> : (
            <Table size="small">
              <TableHead><TableRow><TableCell><strong>Value</strong></TableCell><TableCell><strong>Name</strong></TableCell><TableCell align="right" /></TableRow></TableHead>
              <TableBody>
                {values.map(v => (
                  <TableRow key={v._id} hover>
                    <TableCell><strong>{v.value}</strong></TableCell>
                    <TableCell>{v.name}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setValDlg({ editing: true, _id: v._id, value: v.value, name: v.name })}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteValue(v._id)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions><Button variant="outlined" onClick={() => setValuesFor(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Value create/edit */}
      {valGuard.discardDialog}
      <Dialog open={Boolean(valDlg)} onClose={valGuard.attemptClose} maxWidth="xs" fullWidth>
        <DialogTitle>{valDlg?.editing ? `Edit — ${valDlg.value}` : 'Add Value'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Value" value={valDlg?.value || ''} disabled={valDlg?.editing}
              onChange={e => setValDlg({ ...valDlg, value: e.target.value.toUpperCase() })}
              inputProps={{ style: { textTransform: 'uppercase' } }} size="small" helperText="e.g. SCIENCE" />
            <TextField label="Name" value={valDlg?.name || ''} onChange={e => setValDlg({ ...valDlg, name: e.target.value })} size="small" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={valGuard.attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={saveValue}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DimensionsList;
